// Web Analytics widget — pulls data from self-hosted Umami at analytics.ayudatecno.com.ar
// (via /api/umami backend proxy). Compact summary row at top, one row per
// website, click to expand for sparkline + Pages/Referrers/Countries/Devices
// breakdown. Settings gear to hide sites you don't care about.
//
// Admin-only. Mounted on DashboardPage.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown, ChevronUp, Settings, ExternalLink, AlertCircle, Eye, MousePointerClick, Users, Search, X, Star } from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer, Tooltip as RTooltip, XAxis, LineChart, Line } from 'recharts';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import FeatureSpotlight, { useFeatureSpotlight } from './FeatureSpotlight';

const PERIODS = ['24h', '7d', '30d'];
const HIDDEN_STORAGE_KEY = 'umami_hidden_sites_v1';
const PINNED_STORAGE_KEY = 'umami_pinned_sites_v1';
const ACTIVE_REFRESH_MS = 30000;

function formatNumber(n) {
  if (n == null) return '0';
  const v = Number(n);
  if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
  if (v >= 1000) return (v / 1000).toFixed(1) + 'k';
  return String(v);
}

function formatDuration(seconds) {
  if (!seconds) return '0s';
  const s = Math.round(Number(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem ? `${m}m ${rem}s` : `${m}m`;
}

function bounceRate(stats) {
  if (!stats || !stats.visits) return null;
  return Math.round((Number(stats.bounces) / Number(stats.visits)) * 100);
}

function avgTime(stats) {
  if (!stats || !stats.visits) return 0;
  return Number(stats.totaltime) / Number(stats.visits);
}

function deltaPct(curr, prev) {
  if (!prev || prev === 0) return null;
  return Math.round(((Number(curr) - Number(prev)) / Number(prev)) * 100);
}

// ISO-3166 alpha-2 → flag emoji via regional indicator math.
// Returns the country code itself if it's not 2 letters (Umami occasionally
// has nulls / "Unknown").
function flag(cc) {
  if (!cc || cc.length !== 2) return '🌐';
  const a = cc.toUpperCase().charCodeAt(0);
  const b = cc.toUpperCase().charCodeAt(1);
  if (a < 65 || a > 90 || b < 65 || b > 90) return '🌐';
  return String.fromCodePoint(0x1F1E6 + (a - 65), 0x1F1E6 + (b - 65));
}

function loadSet(key, userId) {
  try {
    const raw = localStorage.getItem(`${key}__${userId || 'anon'}`);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function saveSet(key, userId, set) {
  try {
    localStorage.setItem(`${key}__${userId || 'anon'}`, JSON.stringify(Array.from(set)));
  } catch {
    // ignore
  }
}

export default function WebAnalyticsCard() {
  const { t } = useTranslation();
  const { user } = useAuth() || {};
  const [period, setPeriod] = useState('7d');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [siteDetails, setSiteDetails] = useState({}); // siteId -> { ...detail, period }
  const [showSettings, setShowSettings] = useState(false);
  const [hidden, setHidden] = useState(() => loadSet(HIDDEN_STORAGE_KEY, user?.id));
  const [pinned, setPinned] = useState(() => loadSet(PINNED_STORAGE_KEY, user?.id));
  const [searchQuery, setSearchQuery] = useState('');
  const settingsBtnRef = useRef(null);
  const spotlight = useFeatureSpotlight('web_analytics_v1');

  const fetchOverview = (silent = false) => {
    if (!silent) { setLoading(true); setError(null); }
    return api.get('/umami/overview', { params: { period } })
      .then((r) => setOverview(r.data))
      .catch((err) => {
        if (err.response?.status === 503) setError('not_configured');
        else setError(err.response?.data?.detail || err.message);
      })
      .finally(() => { if (!silent) setLoading(false); });
  };

  useEffect(() => {
    let cancelled = false;
    setOverview(null);
    fetchOverview().then(() => { if (cancelled) setOverview(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  // Refresh active visitor counts (and stats) periodically so the live
  // dot reflects reality without forcing a manual reload.
  useEffect(() => {
    const id = setInterval(() => fetchOverview(true), ACTIVE_REFRESH_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => { saveSet(HIDDEN_STORAGE_KEY, user?.id, hidden); }, [hidden, user?.id]);
  useEffect(() => { saveSet(PINNED_STORAGE_KEY, user?.id, pinned); }, [pinned, user?.id]);

  const toggleHidden = (id) => {
    const next = new Set(hidden);
    if (next.has(id)) next.delete(id); else next.add(id);
    setHidden(next);
  };

  const togglePinned = (id, e) => {
    if (e) e.stopPropagation();
    const next = new Set(pinned);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPinned(next);
  };

  const fetchDetail = (siteId) => {
    api.get(`/umami/site/${siteId}`, { params: { period } })
      .then((r) => setSiteDetails((prev) => ({ ...prev, [siteId]: { ...r.data, period } })))
      .catch((err) => setSiteDetails((prev) => ({ ...prev, [siteId]: { error: err.message, period } })));
  };

  const toggleExpand = (siteId) => {
    if (expandedId === siteId) { setExpandedId(null); return; }
    setExpandedId(siteId);
    const existing = siteDetails[siteId];
    if (!existing || existing.period !== period) fetchDetail(siteId);
  };

  // When period changes, refresh the expanded site's detail
  useEffect(() => {
    if (expandedId) fetchDetail(expandedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  // Visible = not hidden + matches search; pinned sites always render first
  // (sorted by visitors among themselves), then unpinned (sorted by visitors).
  const visibleSites = useMemo(() => {
    if (!overview?.sites) return [];
    const q = searchQuery.trim().toLowerCase();
    const filtered = overview.sites.filter((s) => {
      if (hidden.has(s.id)) return false;
      if (!q) return true;
      return (s.name || '').toLowerCase().includes(q) || (s.domain || '').toLowerCase().includes(q);
    });
    return [...filtered].sort((a, b) => {
      const ap = pinned.has(a.id) ? 1 : 0;
      const bp = pinned.has(b.id) ? 1 : 0;
      if (ap !== bp) return bp - ap;
      return (b.stats?.visitors || 0) - (a.stats?.visitors || 0);
    });
  }, [overview, hidden, pinned, searchQuery]);

  const totals = useMemo(() => {
    const t0 = { pageviews: 0, visitors: 0, visits: 0, active: 0, pageviews_prev: 0, visitors_prev: 0 };
    visibleSites.forEach((s) => {
      if (!s.stats) return;
      t0.pageviews += s.stats.pageviews;
      t0.visitors += s.stats.visitors;
      t0.visits += s.stats.visits;
      t0.pageviews_prev += s.stats.pageviews_prev;
      t0.visitors_prev += s.stats.visitors_prev;
      t0.active += s.active || 0;
    });
    return t0;
  }, [visibleSites]);

  // Aggregate pageviews curve = sum of every site's series at each bucket.
  // All sites use the same period/unit so the x-buckets line up.
  const aggregateSeries = useMemo(() => {
    if (!visibleSites.length) return [];
    const buckets = new Map();
    visibleSites.forEach((s) => {
      (s.series || []).forEach((p) => {
        buckets.set(p.x, (buckets.get(p.x) || 0) + Number(p.y || 0));
      });
    });
    return Array.from(buckets.entries())
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(([x, y]) => ({ x, y }));
  }, [visibleSites]);

  const aggregateSeriesPrev = useMemo(() => {
    if (!visibleSites.length) return [];
    const buckets = new Map();
    visibleSites.forEach((s) => {
      (s.series_prev || []).forEach((p) => {
        buckets.set(p.x, (buckets.get(p.x) || 0) + Number(p.y || 0));
      });
    });
    return Array.from(buckets.entries())
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(([x, y]) => ({ x, y }));
  }, [visibleSites]);

  const showSearch = overview?.sites && overview.sites.length >= 6;

  if (error === 'not_configured') {
    return (
      <div className="card" ref={spotlight.anchorRef} style={{ marginBottom: 16, padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Globe size={18} style={{ color: 'var(--c-brand)' }} />
          <div style={{ fontWeight: 600, fontSize: 14 }}>{t('webAnalytics.title')}</div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>
          {t('webAnalytics.notConfigured')}
        </div>
      </div>
    );
  }

  return (
    <div className="card" ref={spotlight.anchorRef} style={{ marginBottom: 16 }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Globe size={18} style={{ color: 'var(--c-brand)' }} />
          {t('webAnalytics.title')}
          {totals.active > 0 && (
            <span title={t('webAnalytics.liveTooltip', { count: totals.active })} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 4, fontSize: 11, fontWeight: 500, color: '#22c55e' }}>
              <LiveDot />
              {totals.active} {t('webAnalytics.liveNow')}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {showSearch && (
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Search size={12} style={{ position: 'absolute', left: 8, color: 'var(--c-text-muted)', pointerEvents: 'none' }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('webAnalytics.searchPlaceholder')}
                className="form-input"
                style={{ height: 28, fontSize: 12, padding: '0 24px 0 26px', width: 160 }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  style={{ position: 'absolute', right: 4, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--c-text-muted)', padding: 4, display: 'flex' }}
                  aria-label="Clear"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          )}
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              className={`btn btn-sm ${period === p ? 'btn-primary' : 'btn-ghost'}`}
            >
              {t(`webAnalytics.period.${p}`)}
            </button>
          ))}
          <button
            ref={settingsBtnRef}
            type="button"
            onClick={() => setShowSettings((v) => !v)}
            className="btn btn-ghost btn-icon"
            title={t('webAnalytics.showHideSites')}
          >
            <Settings size={14} />
          </button>
        </div>
      </div>

      {/* Aggregate summary across visible sites */}
      {overview && visibleSites.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 12,
          padding: '12px 0 6px',
          borderBottom: '1px solid var(--c-border)',
          marginBottom: 4,
        }}>
          <SummaryStat
            icon={<Users size={18} />}
            label={t('webAnalytics.totalVisitors')}
            value={formatNumber(totals.visitors)}
            delta={deltaPct(totals.visitors, totals.visitors_prev)}
          />
          <SummaryStat
            icon={<Eye size={18} />}
            label={t('webAnalytics.totalPageviews')}
            value={formatNumber(totals.pageviews)}
            delta={deltaPct(totals.pageviews, totals.pageviews_prev)}
          />
          <SummaryStat
            icon={<MousePointerClick size={18} />}
            label={t('webAnalytics.totalVisits')}
            value={formatNumber(totals.visits)}
          />
          <SummaryStat
            icon={<Globe size={18} />}
            label={t('webAnalytics.sitesTracked')}
            value={String(visibleSites.length)}
            hint={hidden.size > 0 ? t('webAnalytics.hiddenHint', { count: hidden.size }) : null}
          />
        </div>
      )}

      {/* Aggregate pageviews curve across all visible sites — with prev-period overlay */}
      {overview && aggregateSeries.length > 1 && (
        <div style={{ padding: '6px 0 10px', borderBottom: '1px solid var(--c-border)', marginBottom: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px 4px' }}>
            <SectionTitle>{t('webAnalytics.combinedPageviews')}</SectionTitle>
            <PrevLegend t={t} />
          </div>
          <Sparkline pageviews={aggregateSeries} pageviewsPrev={aggregateSeriesPrev} height={80} />
        </div>
      )}

      {showSettings && overview?.sites && (
        <div style={{
          padding: 12,
          margin: '12px 0',
          background: 'var(--c-surface-2)',
          border: '1px solid var(--c-border)',
          borderRadius: 6,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            {t('webAnalytics.showHideSites')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
            {overview.sites.map((s) => (
              <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!hidden.has(s.id)}
                  onChange={() => toggleHidden(s.id)}
                />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {loading && !overview && (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>
          {t('common.loading')}
        </div>
      )}

      {error && error !== 'not_configured' && (
        <div style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--c-urgent, #ef4444)', fontSize: 13 }}>
          <AlertCircle size={16} />
          {t('webAnalytics.fetchError')}: {error}
        </div>
      )}

      {overview && visibleSites.length === 0 && !loading && (
        <div style={{ padding: 16, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>
          {t('webAnalytics.allHidden')}
        </div>
      )}

      {overview && visibleSites.length > 0 && (
        <div>
          {visibleSites.map((site) => {
            const isExpanded = expandedId === site.id;
            const detail = siteDetails[site.id];
            const dV = deltaPct(site.stats?.visitors, site.stats?.visitors_prev);
            const dPV = deltaPct(site.stats?.pageviews, site.stats?.pageviews_prev);
            const br = bounceRate(site.stats);
            return (
              <div key={site.id} style={{ borderTop: '1px solid var(--c-border)' }}>
                <div
                  onClick={() => toggleExpand(site.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 12px',
                    cursor: 'pointer',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ flex: '1 1 200px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      type="button"
                      onClick={(e) => togglePinned(site.id, e)}
                      title={pinned.has(site.id) ? t('webAnalytics.unpin') : t('webAnalytics.pin')}
                      style={{
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        padding: 2, display: 'flex', alignItems: 'center',
                        color: pinned.has(site.id) ? '#f59e0b' : 'var(--c-text-muted)',
                        opacity: pinned.has(site.id) ? 1 : 0.5,
                      }}
                      aria-label={t('webAnalytics.pin')}
                    >
                      <Star size={14} fill={pinned.has(site.id) ? '#f59e0b' : 'none'} />
                    </button>
                    {site.active > 0 && <LiveDot title={`${site.active} ${t('webAnalytics.liveNow')}`} />}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {site.name}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--c-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {site.domain}
                      </div>
                    </div>
                  </div>
                  {site.error ? (
                    <div style={{ flex: '1 1 200px', fontSize: 12, color: 'var(--c-urgent, #ef4444)' }}>
                      <AlertCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      {site.error}
                    </div>
                  ) : (
                    <>
                      <MiniSparkline series={site.series} seriesPrev={site.series_prev} />
                      <KPI label={t('webAnalytics.visitors')} value={formatNumber(site.stats?.visitors)} delta={dV} />
                      <KPI label={t('webAnalytics.pageviews')} value={formatNumber(site.stats?.pageviews)} delta={dPV} />
                      <KPI label={t('webAnalytics.bounce')} value={br == null ? '—' : `${br}%`} />
                      <KPI label={t('webAnalytics.avgTime')} value={formatDuration(avgTime(site.stats))} />
                    </>
                  )}
                  {isExpanded ? <ChevronUp size={16} style={{ color: 'var(--c-text-muted)' }} /> : <ChevronDown size={16} style={{ color: 'var(--c-text-muted)' }} />}
                </div>

                {isExpanded && (
                  <div style={{ padding: '8px 12px 14px', background: 'var(--c-surface-2)' }}>
                    {!detail && (
                      <div style={{ padding: 10, fontSize: 12, color: 'var(--c-text-muted)' }}>{t('common.loading')}</div>
                    )}
                    {detail?.error && (
                      <div style={{ padding: 10, fontSize: 12, color: 'var(--c-urgent, #ef4444)' }}>{detail.error}</div>
                    )}
                    {detail && !detail.error && (
                      <>
                        {/* Full-width sparkline with prev-period overlay (uses overview series since detail call doesn't include prev). */}
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <SectionTitle>{t('webAnalytics.pageviewsTrend')}</SectionTitle>
                            <PrevLegend t={t} />
                          </div>
                          <Sparkline pageviews={detail.pageviews} pageviewsPrev={site.series_prev} />
                        </div>
                        {/* 2x2 breakdown grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                          <Breakdown
                            title={t('webAnalytics.topPages')}
                            items={detail.topPages}
                            domain={site.domain}
                            hrefFor={(raw) => raw && raw.startsWith('/') ? `https://${site.domain}${raw}` : null}
                          />
                          <Breakdown
                            title={t('webAnalytics.topReferrers')}
                            items={detail.referrers}
                            fallback={t('webAnalytics.noReferrers')}
                            formatLabel={(x) => x || t('webAnalytics.direct')}
                            hrefFor={(raw) => raw ? (raw.startsWith('http') ? raw : `https://${raw}`) : null}
                          />
                          <Breakdown title={t('webAnalytics.countries')} items={detail.countries} formatLabel={(x) => `${flag(x)} ${x || '—'}`} />
                          <Breakdown title={t('webAnalytics.devices')} items={detail.devices} formatLabel={capitalize} />
                          <Breakdown title={t('webAnalytics.browsers')} items={detail.browsers} formatLabel={capitalize} />
                          <Breakdown title={t('webAnalytics.os')} items={detail.os} formatLabel={(x) => x} />
                        </div>
                      </>
                    )}
                    <div style={{ marginTop: 12, fontSize: 11, textAlign: 'right' }}>
                      <a
                        href={`https://analytics.ayudatecno.com.ar/websites/${site.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: 'var(--c-brand)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        {t('webAnalytics.openInUmami')} <ExternalLink size={11} />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <FeatureSpotlight
        anchorRef={spotlight.anchorRef}
        show={spotlight.shouldShow}
        onDismiss={spotlight.dismiss}
        title={t('webAnalytics.spotlightTitle')}
        body={t('webAnalytics.spotlightBody')}
        placement="top"
      />

      <style>{`
        @keyframes umami-live-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.6); opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

function capitalize(s) {
  if (!s) return '—';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
      {children}
    </div>
  );
}

function LiveDot({ title }) {
  return (
    <span title={title} style={{
      display: 'inline-block',
      width: 8, height: 8, borderRadius: '50%',
      background: '#22c55e',
      boxShadow: '0 0 0 2px rgba(34,197,94,0.25)',
      animation: 'umami-live-pulse 1.8s ease-in-out infinite',
      flexShrink: 0,
    }} />
  );
}

function KPI({ label, value, delta }) {
  const deltaColor = delta == null ? null : (delta > 0 ? '#22c55e' : delta < 0 ? '#ef4444' : 'var(--c-text-muted)');
  return (
    <div style={{ flex: '0 0 auto', minWidth: 78, textAlign: 'right' }}>
      <div style={{ fontSize: 10, color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.1 }}>{value}</div>
      {delta != null && (
        <div style={{ fontSize: 10, color: deltaColor }}>
          {delta > 0 ? '+' : ''}{delta}%
        </div>
      )}
    </div>
  );
}

function SummaryStat({ icon, label, value, delta, hint }) {
  const deltaColor = delta == null ? null : (delta > 0 ? '#22c55e' : delta < 0 ? '#ef4444' : 'var(--c-text-muted)');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 8px' }}>
      <div style={{ color: 'var(--c-brand)', flexShrink: 0 }}>{icon}</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10, color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
          {delta != null && (
            <div style={{ fontSize: 11, color: deltaColor, fontWeight: 500 }}>
              {delta > 0 ? '+' : ''}{delta}%
            </div>
          )}
        </div>
        {hint && <div style={{ fontSize: 10, color: 'var(--c-text-muted)' }}>{hint}</div>}
      </div>
    </div>
  );
}

function PrevLegend({ t }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: 'var(--c-text-muted)' }}>
      <span style={{ display: 'inline-block', width: 14, borderTop: '2px solid var(--c-brand)' }} />
      {t('webAnalytics.legendCurrent')}
      <span style={{ display: 'inline-block', width: 14, borderTop: '2px dashed var(--c-text-muted)', marginLeft: 6 }} />
      {t('webAnalytics.legendPrev')}
    </div>
  );
}

// Merge current + previous series index-aligned so the two lines render
// directly over each other in the chart (real x labels would push the
// dashed prev curve into its own time range and ruin the comparison).
function mergeSeriesAligned(curr, prev) {
  const c = Array.isArray(curr) ? curr : (curr?.pageviews || []);
  const p = Array.isArray(prev) ? prev : (prev?.pageviews || []);
  const len = Math.max(c.length, p.length);
  return Array.from({ length: len }, (_, i) => ({
    t: (c[i]?.x || c[i]?.t || c[i]?.time || ''),
    y: Number(c[i]?.y || c[i]?.value || 0),
    yPrev: Number(p[i]?.y || p[i]?.value || 0),
  }));
}

function Sparkline({ pageviews, pageviewsPrev, height = 110 }) {
  const data = mergeSeriesAligned(pageviews, pageviewsPrev);
  if (!data.length) {
    return <div style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>—</div>;
  }
  const hasPrev = data.some((d) => d.yPrev > 0);
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="umamiGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--c-brand)" stopOpacity={0.45} />
              <stop offset="100%" stopColor="var(--c-brand)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="t" tick={{ fontSize: 10, fill: 'var(--c-text-muted)' }} tickFormatter={(s) => (s || '').slice(5, 10)} interval="preserveStartEnd" />
          <RTooltip
            contentStyle={{ background: 'var(--c-surface)', border: '1px solid var(--c-border)', fontSize: 12 }}
            labelStyle={{ color: 'var(--c-text-muted)' }}
            formatter={(v, name) => [v, name === 'yPrev' ? 'Anterior' : 'Actual']}
          />
          {hasPrev && (
            <Line
              type="monotone"
              dataKey="yPrev"
              stroke="var(--c-text-muted)"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              dot={false}
              isAnimationActive={false}
            />
          )}
          <Area type="monotone" dataKey="y" stroke="var(--c-brand)" strokeWidth={2} fill="url(#umamiGrad)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// Tiny inline sparkline for collapsed rows — no axes, no tooltip, just a
// shape so the eye can compare trends across sites at a glance.
// Dashed previous-period line behind current when prev data exists.
function MiniSparkline({ series, seriesPrev }) {
  const data = mergeSeriesAligned(series, seriesPrev);
  if (data.length < 2) return <div style={{ width: 80, height: 24 }} />;
  const hasPrev = data.some((d) => d.yPrev > 0);
  return (
    <div style={{ width: 80, height: 24, flexShrink: 0 }}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          {hasPrev && (
            <Line
              type="monotone"
              dataKey="yPrev"
              stroke="var(--c-text-muted)"
              strokeWidth={1}
              strokeDasharray="2 2"
              dot={false}
              isAnimationActive={false}
              opacity={0.55}
            />
          )}
          <Line
            type="monotone"
            dataKey="y"
            stroke="var(--c-brand)"
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function Breakdown({ title, items, formatLabel, fallback, domain, hrefFor }) {
  const list = Array.isArray(items) ? items : (items?.data || []);
  return (
    <div>
      <SectionTitle>{title}</SectionTitle>
      {(!list || list.length === 0) ? (
        <div style={{ fontSize: 12, color: 'var(--c-text-muted)', fontStyle: 'italic' }}>{fallback || '—'}</div>
      ) : (
        (() => {
          const top = list.slice(0, 5);
          const max = Math.max(...top.map((p) => Number(p.y || p.value || 0))) || 1;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {top.map((p, i) => {
                const raw = p.x || p.url || p.referrer || '';
                const label = formatLabel ? formatLabel(raw) : raw;
                const count = Number(p.y || p.value || 0);
                const pct = (count / max) * 100;
                const tooltip = domain && raw && raw.startsWith('/') ? `${domain}${raw}` : raw;
                const href = hrefFor ? hrefFor(raw) : null;
                const inner = (
                  <>
                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: 'var(--c-brand)', opacity: 0.12 }} />
                    <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }} title={tooltip || label}>
                        {label || '—'}
                      </span>
                      {href && <ExternalLink size={10} style={{ color: 'var(--c-text-muted)', flexShrink: 0, opacity: 0.6 }} />}
                      <span style={{ fontWeight: 600, flexShrink: 0 }}>{formatNumber(count)}</span>
                    </div>
                  </>
                );
                const baseStyle = { position: 'relative', padding: '4px 6px', fontSize: 12, background: 'var(--c-surface)', borderRadius: 4, overflow: 'hidden', display: 'block', color: 'var(--c-text)', textDecoration: 'none' };
                return href ? (
                  <a key={i} href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={baseStyle}>
                    {inner}
                  </a>
                ) : (
                  <div key={i} style={baseStyle}>{inner}</div>
                );
              })}
            </div>
          );
        })()
      )}
    </div>
  );
}
